import {useEffect} from "react";
import {useNavigate, useParams} from "react-router-dom";

import {useToast} from "@/src/modules/core/hooks";

import ClaimPublicShell from "../components/claim-public-shell";
import ClaimVerifyResult from "../components/claim-verify-result";
import useResidentClaimVerify from "../hooks/use-resident-claim-verify";
import {
  getClaimErrorMessage,
  isClaimDisabledError,
  isClaimExpiredVerifyError,
} from "../lib/claim-errors";
import {setRequiresProfileCompletionPending} from "../lib/claim-profile-pending";

const PAGE_TITLE = "Activate your resident account";

export default function ResidentClaimVerifyPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const {token} = useParams<{token: string}>();
  const verify = useResidentClaimVerify({token});

  useEffect(() => {
    if (!verify.isSuccess || !verify.data) {
      return;
    }

    setRequiresProfileCompletionPending(
      verify.data.data.requires_profile_completion === true,
    );

    toast.success(verify.data.message || "Your account has been activated.");
    navigate("/login", {replace: true});
  }, [verify.isSuccess, verify.data, navigate, toast]);

  let content = null;

  if (verify.isPending) {
    content = <ClaimVerifyResult variant="loading" />;
  } else if (verify.isError) {
    if (isClaimExpiredVerifyError(verify.error)) {
      content = (
        <ClaimVerifyResult
          variant="expired"
          message={getClaimErrorMessage(
            verify.error,
            "This verification link has expired. Please restart the claim.",
          )}
        />
      );
    } else if (isClaimDisabledError(verify.error)) {
      content = (
        <ClaimVerifyResult
          variant="unavailable"
          message={getClaimErrorMessage(
            verify.error,
            "Resident claim is currently disabled.",
          )}
        />
      );
    } else {
      content = (
        <ClaimVerifyResult
          variant="invalid"
          message={getClaimErrorMessage(
            verify.error,
            "This verification link is invalid or has already been used.",
          )}
        />
      );
    }
  } else if (verify.isSuccess) {
    content = <ClaimVerifyResult variant="loading" />;
  }

  return <ClaimPublicShell title={PAGE_TITLE}>{content}</ClaimPublicShell>;
}
